import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { SearchBar } from "../components/SearchBar";
import { LoadingSpinner } from "../components/LoadingSpinner";
import pokeApi, { PokemonSummary } from "../services/pokeApi";
import { usePokedexNav } from "../context/PokedexNavContext";
import { gb, iconSpriteUrl } from "../styles/theme";

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const List = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  overflow: hidden;
`;

const Row = styled.button<{ $active: boolean }>`
  display: grid;
  grid-template-columns: 14px 40px 40px 1fr;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 3px 4px;
  text-align: left;
  background: ${(props) => (props.$active ? gb.dark : "transparent")};
  color: ${(props) => (props.$active ? gb.lightest : gb.dark)};
  border: 0;
  image-rendering: pixelated;

  &:hover {
    background: ${(props) => (props.$active ? gb.dark : gb.mid)};
    color: ${gb.lightest};
  }
`;

const Cursor = styled.span`
  font-size: 8px;
  line-height: 1;
`;

const DexNo = styled.span`
  font-size: 8px;
  letter-spacing: 0;
`;

const Sprite = styled.img`
  width: 40px;
  height: 32px;
  object-fit: contain;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
`;

const Name = styled.span`
  font-size: 8px;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-top: 6px;
  border-top: 2px solid ${gb.dark};
  background: ${gb.lightest};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
`;

const PageMeta = styled.span`
  font-size: 8px;
  color: ${gb.dark};
`;

const NavButton = styled.button`
  min-width: 56px;
  padding: 6px 8px;
  background: ${gb.dark};
  color: ${gb.lightest};
  border: 0;
  font-size: 8px;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover:not(:disabled) {
    background: ${gb.mid};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Status = styled.div`
  font-size: 8px;
  color: ${gb.dark};
  text-align: center;
  padding: 16px 8px;
  line-height: 1.8;
`;

const RetryButton = styled.button`
  margin-top: 12px;
  padding: 8px 12px;
  background: ${gb.dark};
  color: ${gb.lightest};
  border: 0;
  font-size: 8px;
  text-transform: uppercase;

  &:hover {
    background: ${gb.mid};
  }
`;

interface PokemonListProps {
  selectedId: number | null;
  onSelect: (pokemon: PokemonSummary) => void;
}

const DESKTOP_PAGE_SIZE = 7;
const MOBILE_PAGE_SIZE = 5;
const DEX_SIZE = 151;

const PokemonList: React.FC<PokemonListProps> = ({ selectedId, onSelect }) => {
  const { register } = usePokedexNav();
  const [pokemonList, setPokemonList] = useState<PokemonSummary[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 760
      ? MOBILE_PAGE_SIZE
      : DESKTOP_PAGE_SIZE
  );

  const filteredPokemon = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return pokemonList;
    }
    return pokemonList.filter(
      (pokemon) =>
        pokemon.name.toLowerCase().includes(query) ||
        pokemon.id.toString().includes(query) ||
        String(pokemon.id).padStart(3, "0").includes(query)
    );
  }, [pokemonList, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPokemon.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const currentPokemon = filteredPokemon.slice(
    startIndex,
    startIndex + pageSize
  );

  const stateRef = useRef({
    filteredPokemon,
    currentPage,
    selectedId,
    totalPages,
    onSelect,
    pageSize,
  });
  stateRef.current = {
    filteredPokemon,
    currentPage,
    selectedId,
    totalPages,
    onSelect,
    pageSize,
  };

  const fetchPokemon = async () => {
    try {
      setLoading(true);
      setError(null);
      const list = await pokeApi.getPokemonList(DEX_SIZE);
      setPokemonList(list);
      if (list.length > 0) {
        onSelect(list[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load Pokémon");
      setPokemonList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPokemon();
    // Load once on mount. onSelect is stable setState from App.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const apply = () =>
      setPageSize(window.innerWidth <= 760 ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE);
    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    if (filteredPokemon.length > 0) {
      const stillVisible = filteredPokemon.some((p) => p.id === selectedId);
      if (!stillVisible) {
        onSelect(filteredPokemon[0]);
      }
    }
    // Only react to search/list changes, not selection itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, pokemonList]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    return register({
      clearSearch: () => setSearchQuery(""),
      move: (dir) => {
        const {
          filteredPokemon: list,
          selectedId: currentId,
          onSelect: select,
          pageSize: size,
        } = stateRef.current;
        if (list.length === 0) {
          return;
        }

        const index = Math.max(
          0,
          list.findIndex((pokemon) => pokemon.id === currentId)
        );

        if (dir === "up") {
          const next = Math.max(0, index - 1);
          select(list[next]);
          setCurrentPage(Math.floor(next / size) + 1);
          return;
        }

        if (dir === "down") {
          const next = Math.min(list.length - 1, index + 1);
          select(list[next]);
          setCurrentPage(Math.floor(next / size) + 1);
          return;
        }

        if (dir === "left") {
          setCurrentPage((page) => {
            const nextPage = Math.max(1, page - 1);
            const nextIndex = (nextPage - 1) * size;
            select(list[Math.min(nextIndex, list.length - 1)]);
            return nextPage;
          });
          return;
        }

        setCurrentPage((page) => {
          const maxPage = Math.max(1, Math.ceil(list.length / size));
          const nextPage = Math.min(maxPage, page + 1);
          const nextIndex = (nextPage - 1) * size;
          select(list[Math.min(nextIndex, list.length - 1)]);
          return nextPage;
        });
      },
    });
  }, [register]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && pokemonList.length === 0) {
    return (
      <Container>
        <Status>
          {error}
          <br />
          <RetryButton onClick={() => void fetchPokemon()}>Retry</RetryButton>
        </Status>
      </Container>
    );
  }

  return (
    <Container>
      <SearchBar value={searchQuery} onChange={setSearchQuery} />
      {filteredPokemon.length === 0 ? (
        <Status>NO DATA</Status>
      ) : (
        <>
          <List>
            {currentPokemon.map((pokemon) => {
              const active = pokemon.id === selectedId;
              return (
                <Row
                  key={pokemon.id}
                  $active={active}
                  onClick={() => onSelect(pokemon)}
                >
                  <Cursor>{active ? "▶" : ""}</Cursor>
                  <DexNo>#{String(pokemon.id).padStart(3, "0")}</DexNo>
                  <Sprite
                    src={iconSpriteUrl(pokemon.id)}
                    alt=""
                    draggable={false}
                  />
                  <Name>{pokemon.name}</Name>
                </Row>
              );
            })}
          </List>
          <Footer>
            <NavButton
              disabled={currentPage === 1}
              onClick={() => {
                const nextPage = Math.max(1, currentPage - 1);
                setCurrentPage(nextPage);
                const next = filteredPokemon[(nextPage - 1) * pageSize];
                if (next) {
                  onSelect(next);
                }
              }}
            >
              Prev
            </NavButton>
            <PageMeta>
              {currentPage}/{totalPages}
            </PageMeta>
            <NavButton
              disabled={currentPage >= totalPages}
              onClick={() => {
                const nextPage = Math.min(totalPages, currentPage + 1);
                setCurrentPage(nextPage);
                const next = filteredPokemon[(nextPage - 1) * pageSize];
                if (next) {
                  onSelect(next);
                }
              }}
            >
              Next
            </NavButton>
          </Footer>
        </>
      )}
    </Container>
  );
};

export default PokemonList;
